import { useEffect, useMemo, useState } from 'react';
import { Calculator, Camera, Check, ImagePlus, Plus, ScanLine, Search, Undo2, X } from 'lucide-react';
import { uid } from '../state';
import { entryMacros, foodPortion, offToFood } from '../food';
import { FONT_MONO } from '../theme';
import { Button, Input, NumberInput, SectionLabel } from '../ui';
import { FoodRow } from './FoodRow';

/* Fast add: type to search your foods + Open Food Facts, tap Recent, scan a
 * barcode, or photograph the meal. Picking keeps the sheet open so a whole
 * meal goes in one visit; onLog returns the new entry's id, which Undo hands
 * back to onUndo. */
export function QuickAddSheet({ t, token, groupLabel, foods, recents, frequent, freqScores, onLog, onUndo, onDeleteFood, onToggleFavorite, onScan, onPhoto, onLabel, onEstimate, onClose }) {
  const [q, setQ] = useState('');
  const [added, setAdded] = useState([]); // [{ id, name, kcal, protein }] in add order
  const [amounts, setAmounts] = useState({}); // food.id -> amount typed on its row
  // The habit lists as they were when the sheet opened: every add reshuffles
  // them (the food jumps to the top of Recent), and rows must not move under
  // the thumb while a meal goes in.
  const [habits] = useState(() => ({ frequent: frequent || [], recents: recents || [], freqScores }));
  const [remote, setRemote] = useState([]);
  const [loading, setLoading] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [form, setForm] = useState({ name: '', kcal: null, protein: null, carbs: null, fats: null, serving: '' });
  const setF = (patch) => setForm(prev => ({ ...prev, ...patch }));

  const query = q.trim();

  /* What you eat often *in this meal group* is the most useful list here, so
   * it leads — and anything it already shows is dropped from Favorites and
   * Recent below it rather than repeated three times. */
  const shownAbove = useMemo(
    () => new Set(habits.frequent.map(f => (f.name || '').toLowerCase())),
    [habits],
  );
  const favorites = useMemo(
    () => (foods || []).filter(f => f.favorite && !shownAbove.has((f.name || '').toLowerCase())),
    [foods, shownAbove],
  );
  const freshRecents = useMemo(() => {
    const favNames = new Set(favorites.map(f => (f.name || '').toLowerCase()));
    return habits.recents.filter(f => {
      const n = (f.name || '').toLowerCase();
      return !shownAbove.has(n) && !favNames.has(n);
    });
  }, [habits, shownAbove, favorites]);

  /* Search results are ranked by the same per-group habit score, so the thing
   * you were almost certainly reaching for is the top row instead of an
   * alphabetical near-miss. Ties prefer a prefix match, then name. */
  const localMatches = useMemo(() => {
    const ql = query.toLowerCase();
    if (!ql) return [];
    const scores = habits.freqScores || new Map();
    return (foods || [])
      .filter(f => (f.name || '').toLowerCase().includes(ql))
      .sort((a, b) => {
        const an = (a.name || '').toLowerCase();
        const bn = (b.name || '').toLowerCase();
        const byHabit = (scores.get(bn) || 0) - (scores.get(an) || 0);
        if (byHabit) return byHabit;
        const byPrefix = (an.startsWith(ql) ? 0 : 1) - (bn.startsWith(ql) ? 0 : 1);
        return byPrefix || an.localeCompare(bn);
      })
      .slice(0, 25);
  }, [query, foods, habits]);

  // Debounced Open Food Facts search.
  useEffect(() => {
    if (query.length < 2) { setRemote([]); setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    const id = setTimeout(async () => {
      try {
        const res = await fetch(`/api/food/search?q=${encodeURIComponent(query)}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (!cancelled) setRemote(data.success ? (data.results || []).map(offToFood) : []);
      } catch {
        if (!cancelled) setRemote([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(id); };
  }, [query, token]);

  const pick = (food) => {
    const typed = amounts[food.id];
    const item = typed != null && typed > 0 ? { ...food, portion: typed } : food;
    const id = onLog(item);
    if (!id) return;
    const m = entryMacros({ per: item.per, amount: foodPortion(item) });
    setAdded(prev => [...prev, { id, name: (item.name || '').trim().toLowerCase(), kcal: m.kcal, protein: m.protein }]);
  };

  const undo = () => {
    const last = added[added.length - 1];
    if (!last) return;
    onUndo(last.id);
    setAdded(prev => prev.slice(0, -1));
  };

  const addedTotals = added.reduce((acc, a) => ({ kcal: acc.kcal + a.kcal, protein: acc.protein + a.protein }), { kcal: 0, protein: 0 });
  const addedCounts = useMemo(() => {
    const counts = new Map();
    for (const a of added) counts.set(a.name, (counts.get(a.name) || 0) + 1);
    return counts;
  }, [added]);

  // Every list in the sheet renders its rows the same way: amount field,
  // + and a tick for what's already in.
  const row = (f, extra = {}) => (
    <FoodRow key={f.id} t={t} food={f} onPick={pick} {...extra}
      amount={amounts[f.id]} onAmount={v => setAmounts(prev => ({ ...prev, [f.id]: v }))}
      addedCount={addedCounts.get((f.name || '').trim().toLowerCase()) || 0} />
  );
  const libRow = (f) => row(f, { onToggleFavorite, onDelete: onDeleteFood });

  const submitManual = () => {
    const name = form.name.trim();
    if (!name) return;
    pick({
      id: uid('food'), name, brand: '', unit: 'serving',
      per: { kcal: Number(form.kcal) || 0, protein: Number(form.protein) || 0, carbs: Number(form.carbs) || 0, fats: Number(form.fats) || 0 },
      portion: 1, serving: (form.serving || '').trim(), source: 'manual', code: '',
    });
    setForm({ name: '', kcal: null, protein: null, carbs: null, fats: null, serving: '' });
    setManualOpen(false);
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: t.cardBg, borderTopLeftRadius: 16, borderTopRightRadius: 16, width: '100%', maxWidth: 560, height: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 -8px 32px rgba(0,0,0,0.30)' }}>
        {/* Header */}
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${t.divider}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: t.text }}>Add to {groupLabel}</div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: t.textFaint, padding: 4, display: 'flex' }}><X size={18} /></button>
        </div>

        {/* Search, then the four ways in — on their own row so they still fit
            side by side at phone width. */}
        <div style={{ padding: '12px 18px 10px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ position: 'relative' }}>
            <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: t.textFaint, pointerEvents: 'none' }} />
            <Input t={t} autoFocus placeholder="Type what you ate…" value={q} onChange={e => setQ(e.target.value)} style={{ paddingLeft: 32, width: '100%' }} />
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <Button t={t} size="sm" variant="primary" onClick={onScan} title="Scan a barcode" style={{ flex: 1, justifyContent: 'center' }}>
              <ScanLine size={14} /> Scan
            </Button>
            <Button t={t} size="sm" variant="primary" onClick={onPhoto} title="Photograph your meal" style={{ flex: 1, justifyContent: 'center' }}>
              <Camera size={14} /> Photo
            </Button>
            <Button t={t} size="sm" variant="primary" onClick={onLabel} title="Read a nutrition label or app screenshot" style={{ flex: 1, justifyContent: 'center' }}>
              <ImagePlus size={14} /> Label
            </Button>
            <Button t={t} size="sm" variant="primary" onClick={() => onEstimate(query)} title="Estimate a meal from text" style={{ flex: 1, justifyContent: 'center' }}>
              <Calculator size={14} /> Text
            </Button>
          </div>
        </div>

        {/* Results */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {!query && (
            habits.frequent.length > 0 || favorites.length > 0 || freshRecents.length > 0 ? (
              <>
                {habits.frequent.length > 0 && (
                  <>
                    <SectionLabel t={t}>Your usual {groupLabel.toLowerCase()}</SectionLabel>
                    {habits.frequent.map(f => row(f))}
                  </>
                )}
                {favorites.length > 0 && (
                  <>
                    <SectionLabel t={t}>Favorites</SectionLabel>
                    {favorites.map(libRow)}
                  </>
                )}
                {freshRecents.length > 0 && (
                  <>
                    <SectionLabel t={t}>Recent</SectionLabel>
                    {freshRecents.map(f => row(f))}
                  </>
                )}
              </>
            ) : (
              <div style={{ padding: '28px 24px', textAlign: 'center', color: t.textMuted, fontSize: 13, lineHeight: 1.5 }}>
                Type to search your foods and the Open Food Facts database, tap <b>Scan</b> for a barcode, or <b>Label</b> to read the macros off a product screenshot.
              </div>
            )
          )}

          {query && localMatches.length > 0 && (
            <>
              <SectionLabel t={t}>Your foods</SectionLabel>
              {localMatches.map(libRow)}
            </>
          )}

          {query.length >= 2 && (
            <>
              <SectionLabel t={t} right={loading ? <span style={{ fontSize: 11, color: t.textFaint }}>Searching…</span> : null}>
                Food database
              </SectionLabel>
              {!loading && remote.length === 0 ? (
                <div style={{ padding: '10px 18px 4px', color: t.textFaint, fontSize: 12 }}>No online matches.</div>
              ) : remote.map(f => row(f))}
            </>
          )}
        </div>

        {/* Manual add fallback */}
        <div style={{ borderTop: `1px solid ${t.divider}` }}>
          {!manualOpen ? (
            <button onClick={() => setManualOpen(true)} style={{ width: '100%', padding: '12px 18px', background: 'transparent', border: 'none', cursor: 'pointer', color: t.textMuted, fontSize: 13, fontWeight: 600, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Plus size={14} /> Can’t find it? Add manually
            </button>
          ) : (
            <div style={{ padding: '12px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <Input t={t} placeholder="Name" value={form.name} onChange={e => setF({ name: e.target.value })} />
                <Input t={t} placeholder="Serving label (opt.)" value={form.serving} onChange={e => setF({ serving: e.target.value })} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                <NumberInput t={t} value={form.kcal} onChange={v => setF({ kcal: v })} step={10} suffix="kcal" placeholder="0" />
                <NumberInput t={t} value={form.protein} onChange={v => setF({ protein: v })} step={1} suffix="P" placeholder="0" />
                <NumberInput t={t} value={form.carbs} onChange={v => setF({ carbs: v })} step={1} suffix="C" placeholder="0" />
                <NumberInput t={t} value={form.fats} onChange={v => setF({ fats: v })} step={1} suffix="F" placeholder="0" />
              </div>
              <div style={{ fontSize: 10, color: t.textFaint }}>Per serving. Logged and saved to your foods.</div>
              <Button t={t} variant="primary" disabled={!form.name.trim()} onClick={submitManual} style={{ justifyContent: 'center' }}>
                <Plus size={14} /> Add &amp; log
              </Button>
            </div>
          )}
        </div>

        {/* What this visit added, with a way back from a double tap */}
        {added.length > 0 && (
          <div style={{
            borderTop: `1px solid ${t.divider}`, background: t.surfaceAlt,
            padding: '8px 12px 8px 18px', paddingBottom: 'max(8px, env(safe-area-inset-bottom))',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <div aria-live="polite" style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: t.text }}>{added.length} added</div>
              <div style={{ fontSize: 11, color: t.textMuted, fontFamily: FONT_MONO, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {Math.round(addedTotals.kcal)} kcal · {Math.round(addedTotals.protein)} P
              </div>
            </div>
            <Button t={t} onClick={undo} title="Remove the last food added" style={{ minHeight: 40, flexShrink: 0 }}>
              <Undo2 size={14} /> Undo
            </Button>
            <Button t={t} variant="primary" onClick={onClose} style={{ minHeight: 40, flexShrink: 0 }}>
              <Check size={14} /> Done
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
